import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import PageHeader from '../../components/PageHeader';
import { PaginationControls } from '../../components/PaginationControls';
import ReactMultiSelect from '../../components/react-select-alloption';
import { stream_data } from '../../config/stream_data';

const InstituteStudentEnrollment = () => {
  const navigate = useNavigate();
  const [studentEnrollmentData, setStudentEnrollmentData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // Filter options from API
  const [programOptions, setProgramOptions] = useState([]);
  const [divisionOptions, setDivisionOptions] = useState([]);
  const [studentOptions, setStudentOptions] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [filteredStudentOptions, setFilteredStudentOptions] = useState([]);
  const [degreeOptions, setDegreeOptions] = useState([]);
  const [studyingYearOptions, setStudyingYearOptions] = useState([]);
  const [selectedDegree, setSelectedDegree] = useState([]);
  const [selectedStudyingYear, setSelectedStudyingYear] = useState(null);

  // Current filter values for react-select
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // Current filter values for API
  const [currentFilters, setCurrentFilters] = useState({
    program_id: null,
    division: null,
    student_id: null,
    degree_title: null,
    studying_year: null
  });

  // API configuration
  const API_URL = process.env.REACT_APP_API_LMS;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;
  const InstituteId = sessionStorage.getItem('id');
  const Iid = sessionStorage.getItem("instituteId")
  const role = sessionStorage.getItem('role');
  const instituteType = sessionStorage.getItem('instituteType');

  // Check institute type
  const isSchool = instituteType === 'school';
  const isCollege = instituteType === 'college';

  // Storage keys for persistent filters
  const STORAGE_KEYS = {
    FILTERS: 'enrollment_filters',
    SELECTED_OPTIONS: 'enrollment_selected_options',
    CURRENT_PAGE: 'enrollment_current_page'
  };

  // Custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '38px',
      borderColor: state.isFocused ? '#86b7fe' : '#dee2e6',
      boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': {
        borderColor: state.isFocused ? '#86b7fe' : '#dee2e6'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: '#6c757d'
    }),
    clearIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      cursor: 'pointer'
    })
  };

  // Save filters to sessionStorage
  const saveFiltersToStorage = (filters, selectedOptions, page) => {
    try {
      sessionStorage.setItem(STORAGE_KEYS.FILTERS, JSON.stringify(filters));
      sessionStorage.setItem(STORAGE_KEYS.SELECTED_OPTIONS, JSON.stringify(selectedOptions));
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_PAGE, page.toString());
    } catch (error) {
      console.warn('Failed to save filters to storage:', error);
    }
  };

  // Load filters from sessionStorage
  const loadFiltersFromStorage = () => {
    try {
      const savedFilters = sessionStorage.getItem(STORAGE_KEYS.FILTERS);
      const savedSelectedOptions = sessionStorage.getItem(STORAGE_KEYS.SELECTED_OPTIONS);
      const savedPage = sessionStorage.getItem(STORAGE_KEYS.CURRENT_PAGE);

      return {
        filters: savedFilters ? JSON.parse(savedFilters) : { program_id: null, division: null, student_id: null, degree_title: null, studying_year: null },
        selectedOptions: savedSelectedOptions ? JSON.parse(savedSelectedOptions) : { program: null, division: null, student: null },
        page: savedPage ? parseInt(savedPage, 10) : 1
      };
    } catch (error) {
      console.warn('Failed to load filters from storage:', error);
      return {
        filters: { program_id: null, division: null, student_id: null, degree_title: null, studying_year: null },
        selectedOptions: { program: null, division: null, student: null },
        page: 1
      };
    }
  };

  // Clear filters from storage
  const clearFiltersFromStorage = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEYS.FILTERS);
      sessionStorage.removeItem(STORAGE_KEYS.SELECTED_OPTIONS);
      sessionStorage.removeItem(STORAGE_KEYS.CURRENT_PAGE);
    } catch (error) {
      console.warn('Failed to clear filters from storage:', error);
    }
  };

  useEffect(() => {
    const options = Array.from({ length: 26 }, (_, i) => {
      const letter = String.fromCharCode(65 + i);
      return {
        label: letter,
        value: letter
      };
    });
    setDivisionOptions(options);
  }, []);
  useEffect(() => {
    const years = (stream_data?.studying_years || []).map(y => ({ label: y, value: y }));
    setStudyingYearOptions(years);
    const fetchDegreeTitles = async () => {
      try {
        const res = await fetch(`${API_URL}/api/degree-titles?institute_id=${Iid}`, {
          method: 'GET',
          headers: {
            Authorization: API_TOKEN,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          }
        });
        if (!res.ok) return setDegreeOptions([]);
        const result = await res.json();
        const opts = Array.isArray(result?.data) ? result.data.map(d => ({ label: d, value: d })) : [];
        setDegreeOptions(opts);
      } catch (e) {
        setDegreeOptions([]);
      }
    };
    if (isCollege) fetchDegreeTitles();
  }, [API_URL, API_TOKEN, Iid, isCollege]);

  useEffect(() => {
    // Load saved filters on component mount
    const savedData = loadFiltersFromStorage();

    // Set initial state from saved data
    setCurrentFilters(savedData.filters);
    setCurrentPage(savedData.page);

    fetchAllStudents().then(() => {
      restoreSelectedOptions(savedData.selectedOptions);
    });

    fetchEnrollmentData(savedData.page, savedData.filters);
  }, []);

  // Restore selected options after options are loaded
  const restoreSelectedOptions = (savedSelectedOptions) => {
    if (savedSelectedOptions.program && programOptions.length > 0) {
      const program = programOptions.find(p => p.value === savedSelectedOptions.program.value);
      if (program) {
        setSelectedProgram(program);
      }
    }

    // if (isSchool && savedSelectedOptions.division && divisionOptions.length > 0) {
    //   const division = divisionOptions.find(d => d.value === savedSelectedOptions.division.value);
    //   if (division) {
    //     setSelectedDivision(division);
    //   }
    // }

    if (savedSelectedOptions.student && filteredStudentOptions.length > 0) {
      const student = filteredStudentOptions.find(s => s.value === savedSelectedOptions.student.value);
      if (student) {
        setSelectedStudent(student);
      }
    }
  };

  // Update restoreSelectedOptions calls when options change
  useEffect(() => {
    if (programOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.program) {
        const program = programOptions.find(p => p.value === savedData.selectedOptions.program.value);
        if (program) {
          setSelectedProgram(program);
        }
      }
    }
  }, [programOptions]);

  useEffect(() => {
    if (isSchool && divisionOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.division) {
        const division = divisionOptions.find(d => d.value === savedData.selectedOptions.division.value);
        if (division) {
          setSelectedDivision(division);
        }
      }
    }
  }, [divisionOptions, isSchool]);

  useEffect(() => {
    if (filteredStudentOptions.length > 0) {
      const savedData = loadFiltersFromStorage();
      if (savedData.selectedOptions.student) {
        const student = filteredStudentOptions.find(s => s.value === savedData.selectedOptions.student.value);
        if (student) {
          setSelectedStudent(student);
        }
      }
    }
  }, [filteredStudentOptions]);

  // New function to fetch all students from all pages
  const fetchAllStudents = async (pid) => {
    try {
      const programIds = pid != null ? pid : "all";
      const res = await fetch(`${API_URL}/api/students/by-program`, {
        method: "POST",
        headers: {
          Authorization: API_TOKEN,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          institution_id: Iid,
          program_ids: programIds,
        }),
      });

      const data = await res.json();

      if (data.status) {
        const formattedStudents = data.data.map((s) => ({
          label: `${s.first_name} ${s.last_name}`,
          value: s.id,
          program_id: s.program_id,
        }));

        setAllStudents(formattedStudents);
        setStudentOptions(formattedStudents);
        setFilteredStudentOptions(formattedStudents);
      }
    } catch (err) {
      console.error("Student fetch error:", err);
    }
  };

  const fetchEnrollmentData = async (page = 1, filters = {}) => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const queryParams = new URLSearchParams({
        user_id: InstituteId,
        role_name: role || 'Institute',
        page: page
      });

      // Add filter parameters if they exist
      if (filters.program_id) {
        queryParams.append('program_id', filters.program_id);
      }
      if (isSchool && filters.division) {
        queryParams.append('division', filters.division);
      }
      if (filters.student_id) {
        queryParams.append('student_id', filters.student_id);
      }
      if (filters.degree_title) {
        queryParams.append('degree_title', filters.degree_title);
      }
      if (filters.studying_year) {
        queryParams.append('studying_year', filters.studying_year);
      }

      const response = await fetch(`${API_URL}/api/enrollments?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': API_TOKEN,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      // Transform API data to match your component's expected format
      const transformedData = transformApiData(data?.data);
      setStudentEnrollmentData(transformedData);

      // Set pagination information
      if (data.status && data.data && data.data.enrollments) {
        const paginationData = data.data.pagination;
        setCurrentPage(paginationData.current_page);
        setTotalPages(paginationData.last_page);
        setTotalRecords(paginationData.total);
        setPerPage(paginationData.per_page);
      }

      // Set program options from API response (programs shouldn't change much)
      if (data.status && data.data) {
        if (data.data.programs) {
          // Remove duplicates by using a Map with program id as key
          const programsMap = new Map();
          data.data.programs.forEach(program => {
            programsMap.set(program.id, {
              label: program.program_title,
              value: program.id,
              id: program.id
            });
          });

          // Convert Map to array and sort alphabetically
          const programs = Array.from(programsMap.values()).sort((a, b) => a.label.localeCompare(b.label));
          setProgramOptions(programs);
        }
      }

    } catch (err) {
      console.error('Error fetching enrollment data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Transform API response to match your component's data structure
  const transformApiData = (apiResponse) => {
    if (apiResponse.enrollments) {
      const enrollments = apiResponse.enrollments;

      return enrollments.map((item) => ({
        id: item.id,
        student: item.student_name,
        program: item.program_name,
        degree_title: item.degree_title,
        studying_year: item.studying_year,
        division: item.division || 'N/A',
        session: item.session,
        type: item.enrollment_type,
        date: item.enrollment_date,
        enrollmentNumber: item.id,
        institute: item.institution_name,
        creditsUsed: item.credits_used,
        studentId: item.student_id,
        programId: item.program_id,
        institutionId: item.institution_id,
        paymentId: item.payment_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at,
        moduleProgress: item.module_progress,
        serialNo: item.serial_no,
      }));
    }

    return [];
  };

  const handleView = (id) => {
    // Save current filters before navigating
    const selectedOptions = {
      program: selectedProgram,
      division: selectedDivision,
      student: selectedStudent
    };
    saveFiltersToStorage(currentFilters, selectedOptions, currentPage);

    navigate(`/student-enrollment/${id}`);
  };

  const handleRefresh = () => {
    // Clear stored filters on refresh
    clearFiltersFromStorage();
    setFilteredStudentOptions([]);
    setSelectedProgram(null);
    setSelectedDivision(null);
    setSelectedStudent(null);
    setCurrentFilters({ program_id: null, division: null, student_id: null });
    setCurrentPage(1);
    fetchAllStudents();
    fetchEnrollmentData(1, { program_id: null, division: null, student_id: null });
  };

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);

      // Save current state to storage
      const selectedOptions = {
        program_id: selectedProgram?.value || null,
        division: selectedDivision?.value || null,
        student_id: selectedStudent
      };
      saveFiltersToStorage(currentFilters, selectedOptions, page);
      fetchEnrollmentData(page, selectedOptions);
    }
  };

  // Handle program filter change
  const handleProgramChange = (selectedOption) => {
    setSelectedProgram(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.program_id = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedOption,
      division: selectedDivision,
      student: selectedStudent
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchAllStudents(selectedOption ? selectedOption.value : null)
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle division filter change
  const handleDivisionChange = (selectedOption) => {
    setSelectedDivision(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.division = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedProgram,
      division: selectedOption,
      student: selectedStudent
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle student filter change
  const handleStudentChange = (selectedOption) => {
    setSelectedStudent(selectedOption);

    const updatedFilters = { ...currentFilters };
    updatedFilters.student_id = selectedOption ? selectedOption.value : null;

    setCurrentFilters(updatedFilters);

    // Reset to page 1 when applying filters
    const newPage = 1;
    setCurrentPage(newPage);

    // Save filters to storage
    const selectedOptions = {
      program: selectedProgram,
      division: selectedDivision,
      student: selectedOption
    };
    saveFiltersToStorage(updatedFilters, selectedOptions, newPage);

    // Fetch data with new filters
    fetchEnrollmentData(newPage, updatedFilters);
  };

  // Handle filter reset
  const handleFilterReset = () => {
    const resetFilters = {
      program_id: null,
      division: null,
      student_id: null
    };
    setCurrentFilters(resetFilters);
    const newPage = 1;
    setCurrentPage(newPage);
    setSelectedProgram(null);
    setSelectedDivision(null);
    setSelectedStudent(null);

    // Clear stored filters
    clearFiltersFromStorage();

    // Reset student options to show all students
    setFilteredStudentOptions(allStudents);

    fetchEnrollmentData(newPage, resetFilters);
  };

  // Define columns based on instituteType
  const getColumns = () => {
    const baseColumns = [
      { key: 'serialNo', label: 'Sr No.' },
      { key: 'student', label: 'Student' },
      { key: 'program', label: 'Program' }
    ];

    if (isCollege) {
      baseColumns.push({
        key: 'degree_title',
        label: 'Degree Title',
        render: (value, item) => value ? (
          <>
            <div>{value}</div>
            {item.studying_year && (
              <div className="small text-muted">({item.studying_year})</div>
            )}
          </>
        ) : '-'
      });
    }

    // Add division column only for schools
    if (isSchool) {
      baseColumns.push({
        key: 'division',
        label: 'Division',
        render: value => value && value !== 'N/A' ? `Division ${value.toUpperCase()}` : 'N/A'
      });
    }

    // Add remaining columns
    baseColumns.push(
      {
        key: 'moduleProgress',
        label: 'Module Progress',
        render: (value) => {
          if (!value || !value.total_modules) {
            return '-';
          }

          const { completed_modules = 0, total_modules = 0 } = value;
          return `${completed_modules}/${total_modules}`;
        }
      },
      {
        key: 'date',
        label: 'Date',
        render: value => {
          try {
            return new Date(value).toLocaleDateString('en-IN', {
              year: 'numeric',
              month: 'short',
              day: 'numeric'
            });
          } catch (e) {
            return value;
          }
        }
      },
      {
        key: 'actions',
        label: 'Actions',
        render: (_, item) => (
          <button
            className="btn btn-sm text-white"
            style={{ backgroundColor: '#8b5cf6', borderColor: '#8b5cf6' }}
            onClick={() => handleView(item?.studentId)}
          >
            View Progress
          </button>
        )
      }
    );

    return baseColumns;
  };

  const columns = getColumns();


  if (error) {
    return (
      <>
        <div className="row">
          <div className="col-12">
            <PageHeader title="Enrolled Student"
              breadcrumbs={[
                { label: "Dashboard", path: "/institute-dashboard" },
                { label: "Enrolled Student", active: true }
              ]}
            />
          </div>
        </div>

        <div className="row">
          <div className="col-12">
            <div className="alert alert-danger" role="alert">
              <h4 className="alert-heading">Error Loading Data</h4>
              <p>Failed to fetch enrollment data: {error}</p>
              <hr />
              <button className="btn btn-outline-danger" onClick={handleRefresh}>
                Try Again
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="row">
        <div className="col-12">
          <PageHeader title="Enrolled Student"
            breadcrumbs={[
              { label: "Dashboard", path: "/institute-dashboard" },
              { label: "Enrolled Student", active: true }
            ]}
          />
        </div>
      </div>

      {/* Custom Filter Section using React Select */}
      <div className="row mb-4 p-2 rounded" style={{ backgroundColor: '#f8f9fa' }}>
        {/* Program filter - always visible */}
        <div className="col-12 col-sm-6 col-md-4 col-lg mb-3">
          <label className="form-label fw-semibold">Filter by Program:</label>
          <Select
            value={selectedProgram}
            onChange={handleProgramChange}
            options={programOptions}
            placeholder="Select a program..."
            isClearable
            isSearchable
            styles={selectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        {/* Division filter - only show for schools */}
        {isSchool && (
          <div className="col-12 col-sm-6 col-md-4 col-lg mb-3">
            <label className="form-label fw-semibold">Filter by Division:</label>
            <Select
              value={selectedDivision}
              onChange={handleDivisionChange}
              options={divisionOptions}
              placeholder="Select a division..."
              isClearable
              isSearchable
              styles={selectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        )}

        {/* Student filter - always visible */}
        <div className="col-12 col-sm-6 col-md-4 col-lg mb-3">
          <label className="form-label fw-semibold">Filter by Student:</label>
          <Select
            value={selectedStudent}
            onChange={handleStudentChange}
            options={filteredStudentOptions}
            placeholder="Select a student..."
            isClearable
            isSearchable
            styles={selectStyles}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>

        {/* Degree Title - only for colleges */}
        {isCollege && (
          <div className="col-12 col-sm-6 col-md-4 col-lg mb-3">
            <label className="form-label fw-semibold">Degree Title</label>
            <ReactMultiSelect
              value={selectedDegree}
              options={degreeOptions}
              onChange={(vals) => {
                setSelectedDegree(vals || []);
                const updated = { ...currentFilters, degree_title: (vals || []).map(v => v.value).join(',') || null };
                setCurrentFilters(updated);
                const newPage = 1; setCurrentPage(newPage);
                const selectedOptions = { program: selectedProgram, division: selectedDivision, student: selectedStudent };
                saveFiltersToStorage(updated, selectedOptions, newPage);
                fetchEnrollmentData(newPage, updated);
              }}
              placeholder="Select degree title"
            />
          </div>
        )}

        {/* Studying Year - only for colleges */}
        {isCollege && (
          <div className="col-12 col-sm-6 col-md-4 col-lg mb-3">
            <label className="form-label fw-semibold">Studying Year</label>
            <Select
              value={selectedStudyingYear}
              onChange={(val) => {
                setSelectedStudyingYear(val);
                const updated = { ...currentFilters, studying_year: val ? val.value : null };
                setCurrentFilters(updated);
                const newPage = 1; setCurrentPage(newPage);
                const selectedOptions = { program: selectedProgram, division: selectedDivision, student: selectedStudent };
                saveFiltersToStorage(updated, selectedOptions, newPage);
                fetchEnrollmentData(newPage, updated);
              }}
              options={studyingYearOptions}
              placeholder="Select studying year"
              isClearable
              isSearchable
              styles={selectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
        )}

        {/* Reset button - full width on xs, auto width on sm+ */}
        <div className="col-12 col-sm-auto col-md-auto d-flex align-items-end justify-content-start justify-content-sm-end mb-3">
          <button
            className="btn btn-primary w-100 w-sm-auto"
            onClick={handleFilterReset}
            disabled={!selectedProgram && !selectedDivision && !selectedStudent && selectedDegree.length === 0 && !selectedStudyingYear}
          >
            Reset Filters
          </button>
        </div>
      </div>

      {loading ?
        <div className="py-4">
          <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div> : <>
          {/* Entries info */}
          <div className="mb-3">
            <small className="text-muted">
              Showing {totalRecords > 0 ? ((currentPage - 1) * perPage) + 1 : 0} to {Math.min(currentPage * perPage, totalRecords)} of {totalRecords} entries
            </small>
          </div>

          {/* Direct Table Implementation */}
          <div className="row">
            <div className="card rounded-4">
              <div className="">
                {/* Direct Table Implementation */}
                <div className="table-responsive responsive-table">
                  <table className="table table-hover mb-0 fade-in">
                    <thead>
                      <tr>
                        {columns.map(column => (
                          <th key={column.key} className="border-0">
                            {column.label}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {studentEnrollmentData.length > 0 ? (
                        studentEnrollmentData.map((item, index) => (
                          <tr key={item.id || index}>
                            {columns.map(column => (
                              <td key={column.key} data-label={column.label}>
                                {column.render ? column.render(item[column.key], item) : item[column.key]}
                              </td>
                            ))}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={7} className="border-0 p-0 text-center">
                            <div className="empty-state mt-4 mb-3">
                              <div className="icon">
                                <i className="fas fa-clipboard-list text-muted"></i>
                              </div>
                              <h5 className="text-muted mb-2">No Enrollment Records</h5>
                              <p className="text-muted mb-0 small">
                                There are no student enrollment records to display at this time.
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 1 && (
                  <div className="d-flex justify-content-end mt-3 mb-3">
                    <PaginationControls
                      currentPage={currentPage}
                      lastPage={totalPages}
                      onPageChange={(page) => handlePageChange(page)}
                      isEnroll={true}
                    />
                  </div>
                  // <div className="d-flex justify-content-end mt-3">
                  //   <nav>
                  //     <ul className="pagination justify-content-center">
                  //       <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  //         <button
                  //           className="page-link"
                  //           onClick={() => handlePageChange(currentPage - 1)}
                  //           disabled={currentPage === 1}
                  //         >
                  //           Previous
                  //         </button>
                  //       </li>

                  //       <li className={`page-item ${currentPage === 1 ? "active" : ""}`}>
                  //         <button className="page-link" onClick={() => handlePageChange(1)}>
                  //           1
                  //         </button>
                  //       </li>

                  //       {currentPage > 4 && totalPages > 6 && (
                  //         <li className="page-item disabled">
                  //           <span className="page-link">...</span>
                  //         </li>
                  //       )}

                  //       {Array.from({ length: totalPages }, (_, i) => i + 1)
                  //         .filter(
                  //           (page) =>
                  //             page !== 1 &&
                  //             page !== totalPages &&
                  //             page >= currentPage - 1 &&
                  //             page <= currentPage + 1
                  //         )
                  //         .map((page) => (
                  //           <li
                  //             key={page}
                  //             className={`page-item ${currentPage === page ? "active" : ""}`}
                  //           >
                  //             <button className="page-link" onClick={() => handlePageChange(page)}>
                  //               {page}
                  //             </button>
                  //           </li>
                  //         ))}

                  //       {currentPage < totalPages - 2 && totalPages > 5 && (
                  //         <li className="page-item disabled">
                  //           <span className="page-link">...</span>
                  //         </li>
                  //       )}

                  //       {totalPages > 1 && (
                  //         <li
                  //           className={`page-item ${currentPage === totalPages ? "active" : ""}`}
                  //         >
                  //           <button
                  //             className="page-link"
                  //             onClick={() => handlePageChange(totalPages)}
                  //           >
                  //             {totalPages}
                  //           </button>
                  //         </li>
                  //       )}

                  //       <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  //         <button
                  //           className="page-link"
                  //           onClick={() => handlePageChange(currentPage + 1)}
                  //           disabled={currentPage === totalPages}
                  //         >
                  //           Next
                  //         </button>
                  //       </li>
                  //     </ul>
                  //   </nav>
                  // </div>
                )}
              </div>
            </div>
          </div>
        </>}

      <style>
        {`
        .table > thead > tr > th {
            color: black;
            border: none;
            padding: 1rem;
            font-weight: 600;
            text-transform: uppercase;
            font-size: 0.875rem;
            letter-spacing: 0.5px;
          }

          .table > tbody > tr > td {
            padding: 1rem;
            vertical-align: middle;
            border-bottom: 1px solid #e9ecef;
          }

          .table > tbody > tr:last-child > td {
            border-bottom: none;
          }

          @media (max-width: 768px) {
            .table-responsive {
              border: none;
              box-shadow: none;
            }

            .table {
              margin-bottom: 0;
            }

            .table thead {
              display: none;
            }

            .table tbody {
              display: block;
            }

            .table tbody tr {
              display: block;
              margin-bottom: 1rem;
              border: 1px solid #eeeff0;
              margin-top: 10px;
              border-radius: 12px;
              background: white;
              padding: 0;
              overflow: hidden;
              transition: transform 0.2s ease, box-shadow 0.2s ease;
            }

            .table tbody td {
              display: grid;
              gap:4px;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem 1rem;
              border: none !important;
              border-bottom: 1px solid #f1f5f9 !important;
              font-size: 0.9rem;
            }

            .table tbody td:first-child {
              color: black;
              font-weight: 600;
              border-radius: 0 !important;
            }

            .table tbody td:first-child::before {
              color: #64748b;
              font-weight: 600;
              font-size: 0.8rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-right: 0.5rem;
              flex-shrink: 0;
              min-width: 80px;
            }

            .table tbody td:last-child {
              justify-content: center;
              padding: 1rem;
            }

            .table tbody td::before {
              content: attr(data-label);
              font-weight: 600;
              color: #64748b;
              font-size: 0.8rem;
              text-transform: uppercase;
              letter-spacing: 0.5px;
              margin-right: 0.5rem;
              flex-shrink: 0;
              min-width: 80px;
            }

            /* Hide the ACTIONS label - try multiple possible values */
            .table tbody td[data-label="ACTIONS"]::before,
            .table tbody td[data-label="Actions"]::before,
            .table tbody td[data-label="ACTION"]::before {
              display: none !important;
            }

            .table tbody td[data-label="ID"]{
              display: flex !important;
            }

            /* Style for PROGRAM field - allow text wrapping on right side */
            .table tbody td[data-label="PROGRAM"],
            .table tbody td[data-label="Program"],
            .table tbody td[data-label="PROGRAM NAME"] {
              align-items: flex-start !important;
              flex-direction: row !important;
            }

            .table tbody td[data-label="PROGRAM"] .program-text,
            .table tbody td[data-label="Program"] .program-text,
            .table tbody td[data-label="PROGRAM NAME"] .program-text {
              flex: 1;
              text-align: right;
              line-height: 1.3;
              word-wrap: break-word;
              white-space: normal;
              max-width: 60%;
              font-size: 0.85rem;
            }

            /* If no wrapper class, target the content directly */
            .table tbody td[data-label="PROGRAM"] > *:not(::before),
            .table tbody td[data-label="Program"] > *:not(::before),
            .table tbody td[data-label="PROGRAM NAME"] > *:not(::before) {
              flex: 1;
              text-align: right;
              line-height: 1.3;
              word-wrap: break-word;
              white-space: normal;
              max-width: 60%;
              font-size: 0.85rem;
            }

            /* Adjust ACTIONS row to center the button without label */
            .table tbody td[data-label="ACTIONS"],
            .table tbody td[data-label="Actions"],
            .table tbody td[data-label="ACTION"] {
              justify-content: center !important;
              padding: 0.75rem 1rem !important;
            }

            .table tbody td > * {
              margin: 0;
              flex-shrink: 0;
            }

            .table tbody td .btn {
              padding: 0.5rem 1rem;
              font-size: 0.85rem;
              border-radius: 8px;
              font-weight: 500;
            }

            .empty-state {
              text-align: center;
              padding: 3rem 1rem;
              background: white;
              border-radius: 12px;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            }

            .empty-state .icon {
              font-size: 3rem;
              margin-bottom: 1rem;
              opacity: 0.5;
            }
          }

          @media (min-width: 769px) {
            .table > tbody > tr {
              transition: all 0.2s ease;
            }
          }

          .table-loading {
            opacity: 0.7;
            pointer-events: none;
          }

          .fade-in {
            animation: fadeIn 0.3s ease-in;
          }

          @keyframes fadeIn {
            from { 
              opacity: 0; 
              transform: translateY(10px); 
            }
            to { 
              opacity: 1; 
              transform: translateY(0); 
            }
          }
        `}
      </style>

    </>
  );
};

export default InstituteStudentEnrollment;
